import { useState } from 'react';
import { EMPRESA, dataDoRecibo } from '../../design/empresa';
import { valorPorExtenso } from '../../design/extenso';
import { MOTIVO_DO_CORTE, composicao, diaCurto, mesPorExtenso, reais } from '../../premiacao';
import type { ChequeDaPessoa, ChequeDaSemana } from '../../premiacao';
import { NOME_DO_PROGRAMA, PROGRAMAS, contas, somaQtd } from './conta-da-semana';
import { nomeDaSemana } from '../../design/semana';

const CURTO = Object.fromEntries(PROGRAMAS.map((x) => [x.id, x.curto]));
// a folha usa o CSS de impressão da Conferência (cf-folha…) — também quando sai da Premiação
import './conferencia.css';
import './folhas-a4.css';

/**
 * FOLHAS A4 DA PREMIAÇÃO (pedido do dono, 27/09/2026): quatro modelos de cheque/comprovante para
 * entregar aos balconistas — os números são os do cheque do servidor (os mesmos da tela):
 *   completo  — 1 balconista por folha (pode virar mais de uma página): tudo, item por item;
 *   umaFolha  — o completo apertado numa A4 só (pedido do dono, 02/10): itens somados por produto;
 *   detalhado — 1 por folha: resumo por programa + o que gerou, produto a produto;
 *   resumido  — 2 por folha: totais por programa, descontos, a receber e assinaturas;
 *   compacto  — 4 por folha, com linha de corte: o canhoto — nome, semana, valor e assinatura.
 */

export type ModeloDaFolha = 'completo' | 'umaFolha' | 'detalhado' | 'resumido' | 'compacto';

export const MODELOS: readonly {
  readonly id: ModeloDaFolha;
  readonly nome: string;
  readonly porFolha: string;
  readonly descricao: string;
}[] = [
  {
    id: 'completo',
    nome: 'Completo',
    porFolha: '1 por folha (ou mais)',
    descricao:
      'Tudo da semana: contas, o que não entrou e por quê, cada item conferido, devoluções, canceladas e recibo.',
  },
  {
    id: 'umaFolha',
    nome: 'Completo em 1 folha',
    porFolha: '1 por folha, sempre',
    descricao:
      'O completo apertado numa A4: contas, o que não entrou, itens somados por produto, devoluções, canceladas e recibo.',
  },
  {
    id: 'detalhado',
    nome: 'Detalhado',
    porFolha: '1 por folha',
    descricao:
      'Resumo por programa, o que gerou produto a produto, descontos, recibo e assinaturas.',
  },
  {
    id: 'resumido',
    nome: 'Resumido',
    porFolha: '2 por folha',
    descricao: 'Totais por programa, descontos, meta, a receber por extenso e assinaturas.',
  },
  {
    id: 'compacto',
    nome: 'Compacto',
    porFolha: '4 por folha',
    descricao: 'O canhoto: nome, semana, valor por extenso e assinatura. Com linha de corte.',
  },
];

const CHAVE_DO_MODELO = 'inovapay2.folha-a4.modelo';
function modeloGuardado(): ModeloDaFolha {
  try {
    const v = localStorage.getItem(CHAVE_DO_MODELO);
    return MODELOS.some((m) => m.id === v) ? (v as ModeloDaFolha) : 'detalhado';
  } catch {
    return 'detalhado';
  }
}

/** Escolher o modelo antes de imprimir (lembra o último neste aparelho). */
export function EscolherModelo({
  quantas,
  aoImprimir,
  aoFechar,
}: {
  quantas: number;
  aoImprimir: (m: ModeloDaFolha) => void;
  aoFechar: () => void;
}) {
  const [m, setM] = useState<ModeloDaFolha>(modeloGuardado);
  const imprimir = () => {
    try {
      localStorage.setItem(CHAVE_DO_MODELO, m);
    } catch {
      /* sem armazenamento: só não lembra */
    }
    aoImprimir(m);
  };
  return (
    <div className="pr-modal-fundo" role="dialog" aria-label="Modelo da folha A4">
      <div className="pr-modal fa-escolha">
        <h3>Imprimir {quantas === 1 ? 'a folha' : `${String(quantas)} folhas`} — qual modelo?</h3>
        <div className="fa-modelos" role="radiogroup">
          {MODELOS.map((x) => (
            <label key={x.id} className={`fa-modelo${m === x.id ? ' fa-sel' : ''}`}>
              <input
                type="radio"
                name="modelo-da-folha"
                checked={m === x.id}
                onChange={() => setM(x.id)}
              />
              <span className={`fa-miniatura fa-mini-${x.id}`} aria-hidden="true">
                {Array.from({ length: x.id === 'compacto' ? 4 : x.id === 'resumido' ? 2 : 1 }).map(
                  (_, i) => (
                    <i key={i} />
                  ),
                )}
              </span>
              <span className="fa-modelo-texto">
                <b>{x.nome}</b> <em>{x.porFolha}</em>
                <span>{x.descricao}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="pr-modal-acoes">
          <button type="button" className="pr-botao" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="pr-botao pr-botao-vermelho" onClick={imprimir} autoFocus>
            Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}

/** As folhas de um período, no modelo escolhido (só existem na impressão). */
export function FolhasA4({
  modelo,
  pessoas,
  semana,
  semRelatorio,
}: {
  modelo: ModeloDaFolha;
  pessoas: readonly ChequeDaPessoa[];
  semana: ChequeDaSemana;
  semRelatorio: ReadonlySet<string>;
}) {
  return (
    <div className={`cf-folhas fa-${modelo}`}>
      {pessoas.map((c) =>
        modelo === 'compacto' ? (
          <Canhoto key={c.pessoaId} c={c} semana={semana} />
        ) : modelo === 'umaFolha' ? (
          <UmaFolha key={c.pessoaId} c={c} semana={semana} semRelatorio={semRelatorio} />
        ) : modelo === 'resumido' ? (
          <Resumida key={c.pessoaId} c={c} semana={semana} />
        ) : (
          <Inteira
            key={c.pessoaId}
            c={c}
            semana={semana}
            semRelatorio={semRelatorio}
            completo={modelo === 'completo'}
          />
        ),
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * pedaços comuns
 * ------------------------------------------------------------------ */

const periodoPorExtenso = (s: ChequeDaSemana) =>
  `${diaCurto(s.periodo.de)}/${s.periodo.de.slice(0, 4)} a ${diaCurto(s.periodo.ate)}/${s.periodo.ate.slice(0, 4)}`;
const cortadoTotal = (c: ChequeDaPessoa) => (c.cortadoNoMes ?? 0) + (c.descontoDeCorte ?? 0);

function Contas({ c, semRelatorio }: { c: ChequeDaPessoa; semRelatorio?: ReadonlySet<string> }) {
  const k = semRelatorio ? contas(c, semRelatorio) : null;
  return (
    <table data-sem-ordenar className="cf-folha-tabela">
      <thead>
        <tr>
          <th>Programa</th>
          <th>Itens conferidos</th>
          <th>Prêmio</th>
        </tr>
      </thead>
      <tbody>
        {PROGRAMAS.map((x) => (
          <tr key={x.id}>
            <td>{x.nome}</td>
            <td>{somaQtd(c.linhas.bate.filter((l) => l.programa === x.id))}</td>
            <td>{reais(c.tipos[x.id].devido + c.tipos[x.id].cancelado)}</td>
          </tr>
        ))}
        <tr className="cf-folha-sub">
          <td>Rendeu</td>
          <td>{k ? k.confirmado : somaQtd(c.linhas.bate)}</td>
          <td>{reais(c.rendeuBruto)}</td>
        </tr>
        <tr>
          <td>(−) Canceladas</td>
          <td>{somaQtd(c.linhas.canceladas)}</td>
          <td>{reais(-c.cancelado)}</td>
        </tr>
        <tr>
          <td>(−) Devoluções</td>
          <td>{somaQtd(c.linhas.devolvidos)}</td>
          <td>{reais(-c.devolucao)}</td>
        </tr>
        {cortadoTotal(c) > 0 && (
          <tr>
            <td>(−) Vitamina do mês cortada</td>
            <td>—</td>
            <td>{reais(-cortadoTotal(c))}</td>
          </tr>
        )}
        <tr>
          <td>Meta da semana{c.meta > 0 ? ` (${String(c.meta)} itens)` : ''}</td>
          <td>{c.meta > 0 ? c.itensLiquidos : '—'}</td>
          <td>{c.meta === 0 ? 'sem meta' : c.metaBatida ? 'batida' : 'NÃO batida — zera'}</td>
        </tr>
        <tr className="cf-folha-total">
          <td colSpan={2}>A RECEBER</td>
          <td>{reais(c.aReceber)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Recibo({ c, semana }: { c: ChequeDaPessoa; semana: ChequeDaSemana }) {
  return (
    <>
      <div className="cf-recibo">
        <p>
          Recebi de <b>{EMPRESA.razao}</b> (CNPJ {EMPRESA.cnpj}) a quantia de{' '}
          <b>{reais(c.aReceber)}</b> ({valorPorExtenso(c.aReceber)}), referente à premiação do
          InovaPay da semana de {diaCurto(semana.periodo.de)} a {diaCurto(semana.periodo.ate)}.
        </p>
        <span>{dataDoRecibo()}</span>
      </div>
      <div className="cf-folha-assinaturas">
        <div>
          <span />
          entregue por
        </div>
        <div>
          <span />
          recebi — {c.nome}
        </div>
      </div>
    </>
  );
}

function Pagamento({ c, semana }: { c: ChequeDaPessoa; semana: ChequeDaSemana }) {
  const pago = semana.pagamentos[c.pessoaId];
  return (
    <>
      {pago && !pago.estornado
        ? `Pago em ${new Date(pago.pagoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} por ${pago.pagoPor}.`
        : 'Ainda não pago.'}{' '}
      Impresso em {new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}.
    </>
  );
}

/** "Não entrou na conta": regra, itens e quanto deixou de render. */
function perdasDe(
  c: ChequeDaPessoa,
  semRelatorio: ReadonlySet<string>,
): [string, number, number][] {
  const k = contas(c, semRelatorio);
  return [
    ['Vendeu e não lançou', k.naoLancou, c.linhas.naoLancou.reduce((s, l) => s + l.perdeu, 0)],
    ['Lançou em dia diferente', k.diaErrado, c.linhas.diaErrado.reduce((s, l) => s + l.perdeu, 0)],
    [
      'Lote vendido no dia em que entrou',
      k.loteDoDia,
      (c.linhas.loteDoMesmoDia ?? []).reduce((s, l) => s + l.perdeu, 0),
    ],
    [
      'Lançou e não vendeu (não paga)',
      k.naoVendeu,
      c.linhas.naoVendeu.filter((l) => !semRelatorio.has(l.dia)).reduce((s, l) => s + l.pago, 0),
    ],
    [
      'Abaixo do card (não premia)',
      k.abaixo,
      c.linhas.abaixoDoCard.reduce((s, l) => s + l.pago, 0),
    ],
  ];
}

/* ------------------------------------------------------------------ *
 * completo e detalhado — uma pessoa por folha
 * ------------------------------------------------------------------ */

function Inteira({
  c,
  semana,
  semRelatorio,
  completo,
}: {
  c: ChequeDaPessoa;
  semana: ChequeDaSemana;
  semRelatorio: ReadonlySet<string>;
  completo: boolean;
}) {
  const perdas = perdasDe(c, semRelatorio);
  const itens = [...c.linhas.bate].sort((a, b) => a.dia.localeCompare(b.dia));
  return (
    <article className="cf-folha">
      <header className="cf-folha-topo">
        <div>
          <div className="cf-folha-marca">
            {EMPRESA.fantasia} · {EMPRESA.razao} · CNPJ {EMPRESA.cnpj}
          </div>
          <h1>Folha de premiação — InovaPay</h1>
          <div>
            Semana de {periodoPorExtenso(semana)} ({nomeDaSemana(semana.periodo)})
          </div>
        </div>
        <div className="cf-folha-quem">
          <strong>{c.nome}</strong>
          <span>código {c.codigo ?? '—'}</span>
        </div>
      </header>

      <Contas c={c} semRelatorio={semRelatorio} />

      {(c.cortes ?? []).map((x) => (
        <p key={x.id} className="fa-corte">
          <b>Vitamina de {mesPorExtenso(x.mes)} cortada</b> — {MOTIVO_DO_CORTE[x.tipo]}. {x.detalhe}
        </p>
      ))}

      {completo ? (
        <>
          <h2>Não entrou na conta (regras)</h2>
          <table data-sem-ordenar className="cf-folha-tabela">
            <tbody>
              {perdas.map(([t, n, v]) => (
                <tr key={t}>
                  <td>{t}</td>
                  <td>{n} item(ns)</td>
                  <td>{n ? reais(v) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {itens.length > 0 && (
            <>
              <h2>Itens conferidos com o HOS</h2>
              <table data-sem-ordenar className="cf-folha-tabela cf-folha-itens">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Programa</th>
                    <th>Produto</th>
                    <th>Qtd</th>
                    <th>Preço</th>
                    <th>Prêmio</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((l, i) => (
                    <tr key={`${l.dia}${l.produto.nome}${String(i)}`}>
                      <td>{diaCurto(l.dia)}</td>
                      <td>{NOME_DO_PROGRAMA[l.programa]}</td>
                      <td>{l.produto.nome}</td>
                      <td>{l.qtd}</td>
                      <td>{reais(l.precoUnit)}</td>
                      <td>{l.cortadaPeloMes ? 'cortada' : reais(l.devido)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {c.linhas.devolvidos.length > 0 && (
            <>
              <h2>Devoluções que descontaram</h2>
              <table data-sem-ordenar className="cf-folha-tabela cf-folha-itens">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Cupom</th>
                    <th>Produto</th>
                    <th>Qtd</th>
                    <th>Desconto</th>
                  </tr>
                </thead>
                <tbody>
                  {c.linhas.devolvidos.map((l, i) => (
                    <tr key={`${l.cupom}${String(i)}`}>
                      <td>{diaCurto(l.dia)}</td>
                      <td>{l.cupom.split('|').pop()}</td>
                      <td>{l.produto.nome}</td>
                      <td>{l.qtd}</td>
                      <td>{reais(-l.desconto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {c.linhas.canceladas.length > 0 && (
            <>
              <h2>Vendas canceladas</h2>
              <table data-sem-ordenar className="cf-folha-tabela cf-folha-itens">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Produto</th>
                    <th>Qtd</th>
                    <th>Motivo</th>
                    <th>Desconto</th>
                  </tr>
                </thead>
                <tbody>
                  {c.linhas.canceladas.map((l, i) => (
                    <tr key={`${l.dia}${l.produto.nome}${String(i)}`}>
                      <td>{diaCurto(l.dia)}</td>
                      <td>{l.produto.nome}</td>
                      <td>{l.qtd}</td>
                      <td>{l.motivo ?? '—'}</td>
                      <td>{reais(-l.desconto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : (
        <>
          <h2>O que gerou, produto a produto</h2>
          <table data-sem-ordenar className="cf-folha-tabela cf-folha-itens">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Qtd</th>
                <th>Regra</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {composicao(c).map((l, i) => (
                <tr key={i} className={l.desconto ? 'fa-desconto' : ''}>
                  <td>{l.produto}</td>
                  <td>{l.qtd || '—'}</td>
                  <td>{l.regra}</td>
                  <td>{reais(l.gerado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="cf-folha-pe">
        <Pagamento c={c} semana={semana} /> Conta do servidor (lançamentos × vendas do HOS).
      </p>
      <Recibo c={c} semana={semana} />
      <p className="cf-folha-aviso">
        Não é cheque bancário. Comprovante interno de premiação do programa InovaPay.
      </p>
    </article>
  );
}

/* ------------------------------------------------------------------ *
 * resumido — duas por folha
 * ------------------------------------------------------------------ */

function Resumida({ c, semana }: { c: ChequeDaPessoa; semana: ChequeDaSemana }) {
  return (
    <article className="fa-meia">
      <header className="fa-meia-topo">
        <div>
          <div className="cf-folha-marca">
            {EMPRESA.fantasia} · InovaPay · CNPJ {EMPRESA.cnpj}
          </div>
          <b>Premiação da semana {periodoPorExtenso(semana)}</b>
        </div>
        <div className="cf-folha-quem">
          <strong>{c.nome}</strong>
          <span>código {c.codigo ?? '—'}</span>
        </div>
      </header>
      <Contas c={c} />
      <Recibo c={c} semana={semana} />
    </article>
  );
}

/* ------------------------------------------------------------------ *
 * compacto — quatro canhotos por folha
 * ------------------------------------------------------------------ */

function Canhoto({ c, semana }: { c: ChequeDaPessoa; semana: ChequeDaSemana }) {
  return (
    <article className="fa-canhoto">
      <div className="fa-canhoto-esq">
        <div className="cf-folha-marca">{EMPRESA.fantasia} · InovaPay</div>
        <strong>{c.nome}</strong>
        <span>código {c.codigo ?? '—'}</span>
        <span>semana {periodoPorExtenso(semana)}</span>
      </div>
      <div className="fa-canhoto-meio">
        <p>
          Recebi de <b>{EMPRESA.razao}</b> a quantia de <b>{valorPorExtenso(c.aReceber)}</b>,
          referente à premiação do InovaPay desta semana.
        </p>
        <div className="fa-canhoto-assina">
          <span>{dataDoRecibo()}</span>
          <i />
          <span>assinatura — {c.nome}</span>
        </div>
      </div>
      <div className="fa-canhoto-valor">
        <span>A RECEBER</span>
        <b>{reais(c.aReceber)}</b>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ *
 * completo em 1 folha — o completo apertado numa A4 (dono, 02/10)
 * ------------------------------------------------------------------ */

/** Linhas que cabem na A4 junto com o resto; o que passar vira "+ N outros" com a soma. */
const MAX_PRODUTOS = 36;
const MAX_DESCONTOS = 6;

function UmaFolha({
  c,
  semana,
  semRelatorio,
}: {
  c: ChequeDaPessoa;
  semana: ChequeDaSemana;
  semRelatorio: ReadonlySet<string>;
}) {
  const perdas = perdasDe(c, semRelatorio).filter(([, n]) => n > 0);
  // itens conferidos somados por produto + programa: a semana inteira cabe numa folha
  const grupos = new Map<
    string,
    { produto: string; programa: string; qtd: number; premio: number; cortada: boolean }
  >();
  for (const l of c.linhas.bate) {
    const chave = `${l.programa}|${l.produto.nome}`;
    const g = grupos.get(chave) ?? {
      produto: l.produto.nome,
      programa: CURTO[l.programa] ?? NOME_DO_PROGRAMA[l.programa],
      qtd: 0,
      premio: 0,
      cortada: false,
    };
    g.qtd += l.qtd;
    g.premio = Math.round((g.premio + l.devido) * 100) / 100;
    g.cortada ||= !!l.cortadaPeloMes;
    grupos.set(chave, g);
  }
  const todos = [...grupos.values()].sort(
    (a, b) => b.premio - a.premio || a.produto.localeCompare(b.produto),
  );
  const cabem = todos.length > MAX_PRODUTOS ? todos.slice(0, MAX_PRODUTOS - 1) : todos;
  const sobra = todos.slice(cabem.length);
  const descontos = [
    ...c.linhas.devolvidos.map((l) => ({
      tipo: 'Devolução',
      dia: l.dia,
      produto: l.produto.nome,
      qtd: l.qtd,
      valor: l.desconto,
    })),
    ...c.linhas.canceladas.map((l) => ({
      tipo: `Cancelada${l.motivo ? ` (${l.motivo})` : ''}`,
      dia: l.dia,
      produto: l.produto.nome,
      qtd: l.qtd,
      valor: l.desconto,
    })),
  ].sort((a, b) => a.dia.localeCompare(b.dia));
  const descCabem =
    descontos.length > MAX_DESCONTOS ? descontos.slice(0, MAX_DESCONTOS - 1) : descontos;
  const descSobra = descontos.slice(descCabem.length);
  return (
    <article className="fa-uma">
      <header className="fa-meia-topo">
        <div>
          <div className="cf-folha-marca">
            {EMPRESA.fantasia} · {EMPRESA.razao} · CNPJ {EMPRESA.cnpj}
          </div>
          <b>Folha de premiação — InovaPay · semana {periodoPorExtenso(semana)}</b>
        </div>
        <div className="cf-folha-quem">
          <strong>{c.nome}</strong>
          <span>código {c.codigo ?? '—'}</span>
        </div>
      </header>

      <div className="fa-uma-duas">
        <Contas c={c} semRelatorio={semRelatorio} />
        <div>
          <h2>Não entrou na conta (regras)</h2>
          {perdas.length ? (
            <table data-sem-ordenar className="cf-folha-tabela">
              <tbody>
                {perdas.map(([t, n, v]) => (
                  <tr key={t}>
                    <td>{t}</td>
                    <td>{n} item(ns)</td>
                    <td>{reais(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="fa-uma-nada">Nada ficou de fora nesta semana.</p>
          )}
          {(c.cortes ?? []).map((x) => (
            <p key={x.id} className="fa-corte">
              <b>Vitamina de {mesPorExtenso(x.mes)} cortada</b> — {MOTIVO_DO_CORTE[x.tipo]}.{' '}
              {x.detalhe}
            </p>
          ))}
        </div>
      </div>

      <h2>
        Itens conferidos com o HOS — somados por produto ({somaQtd(c.linhas.bate)} itens,{' '}
        {todos.length} produtos)
      </h2>
      {todos.length ? (
        <div className="fa-uma-itens">
          {cabem.map((g) => (
            <div key={`${g.programa}${g.produto}`} className="fa-uma-item">
              <span className="fa-uma-prod">{g.produto}</span>
              <em>{g.programa}</em>
              <span>{g.qtd}</span>
              <span>{g.cortada && !g.premio ? 'cortada' : reais(g.premio)}</span>
            </div>
          ))}
          {sobra.length > 0 && (
            <div className="fa-uma-item fa-uma-sobra">
              <span className="fa-uma-prod">+ {sobra.length} outros produtos</span>
              <em />
              <span>{sobra.reduce((a, g) => a + g.qtd, 0)}</span>
              <span>{reais(sobra.reduce((a, g) => a + g.premio, 0))}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="fa-uma-nada">Nenhum item conferido nesta semana.</p>
      )}

      {descontos.length > 0 && (
        <>
          <h2>Devoluções e canceladas que descontaram</h2>
          <table data-sem-ordenar className="cf-folha-tabela cf-folha-itens">
            <tbody>
              {descCabem.map((l, i) => (
                <tr key={`${l.dia}${l.produto}${String(i)}`}>
                  <td>{diaCurto(l.dia)}</td>
                  <td>{l.tipo}</td>
                  <td>{l.produto}</td>
                  <td>{l.qtd}</td>
                  <td>{reais(-l.valor)}</td>
                </tr>
              ))}
              {descSobra.length > 0 && (
                <tr>
                  <td colSpan={3}>+ {descSobra.length} outros</td>
                  <td>{descSobra.reduce((a, l) => a + l.qtd, 0)}</td>
                  <td>{reais(-descSobra.reduce((a, l) => a + l.valor, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      <div className="fa-uma-fim">
        <p className="cf-folha-pe">
          <Pagamento c={c} semana={semana} /> Conta do servidor (lançamentos × vendas do HOS).
        </p>
        <Recibo c={c} semana={semana} />
        <p className="cf-folha-aviso">
          Não é cheque bancário. Comprovante interno de premiação do programa InovaPay.
        </p>
      </div>
    </article>
  );
}
